import { Text as RNText, TextProps, Platform } from "react-native"
import { cn } from "@src/utils"

export interface TypographyProps extends TextProps {
  variant?: "h1" | "h2" | "h3" | "h4" | "p" | "blockquote" | "lead" | "large" | "small" | "muted"
  className?: string
}

export function Text({ className, variant = "p", ...props }: TypographyProps) {
  // Base text alignment logic from original core.tsx
  const hasAlignment = className?.includes("text-left") || className?.includes("text-right") || className?.includes("text-center")
  const baseAlignment = hasAlignment ? "" : "text-left"

  // Platform specific alignment adjustment (Web/RTL specific from core.tsx)
  // If we are on web, 'text-left' might need to be 'text-right' depending on dir,
  // but core.tsx simply replaced text-left with text-right for web. We keep this logic.
  let alignmentClass = baseAlignment
  if (Platform.OS === "web" && alignmentClass.includes("text-left")) {
    alignmentClass = alignmentClass.replace("text-left", "text-right")
  }

  const variants = {
    h1: "scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl",
    h2: "scroll-m-20 border-b border-gray-200 pb-2 text-3xl font-semibold tracking-tight first:mt-0",
    h3: "scroll-m-20 text-2xl font-semibold tracking-tight",
    h4: "scroll-m-20 text-xl font-semibold tracking-tight",
    p: "leading-7",
    blockquote: "mt-6 border-l-2 border-gray-300 pl-6 italic",
    lead: "text-xl text-gray-500",
    large: "text-lg font-semibold",
    small: "text-sm font-medium leading-none",
    muted: "text-sm text-gray-500",
  }

  const variantClass = variants[variant]

  // Modify alignment class inside cn to handle the web replace logic if needed directly on the full string,
  // but simpler to just compose.
  let finalClass = cn("text-gray-900", variantClass, alignmentClass, className)

  if (Platform.OS === "web") {
    finalClass = finalClass.replace("text-left", "text-right")
  }

  return <RNText className={finalClass} {...props} />
}
